import { IconButton, Text, type DataTableColumn } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { hatcheryTextModes } from "@/app/theme";
import { formatDateTime, parseUtcMs } from "@/utils/datetime";
import { GpuQuotaRequestStatusChip } from "./GpuQuotaRequestStatusChip";
import type { GpuQuotaRequestTableRow } from "./GpuQuotaRequestTableRow";
import styles from "./GpuQuotaPage.module.css";

type GpuQuotaRequestColumnsOptions = {
  // 요청자 철회. 표시 여부는 서버가 준 canCancel 로만 판정한다.
  onCancel: (row: GpuQuotaRequestTableRow) => void;
  // admin 종결. 승인(applied)인지 거절(rejected)인지를 전달한다.
  onResolve: (row: GpuQuotaRequestTableRow, status: "applied" | "rejected") => void;
};

export function createGpuQuotaRequestColumns({
  onCancel,
  onResolve,
}: GpuQuotaRequestColumnsOptions): readonly DataTableColumn<GpuQuotaRequestTableRow>[] {
  return [
    {
      key: "workspaceName",
      header: "Workspace",
      width: "minmax(160px, 1fr)",
      sortable: "both",
      render: (row) => (
        <span className={styles.workspaceCell}>
          <span className={styles.workspaceName}>{row.workspaceName}</span>
          <span className={styles.workspaceId}>{row.workspaceId}</span>
          {/* 지금 이 workspace 를 가진 사람. 아래 "요청자"(접수한 사람)와 다르다. */}
          {row.ownerName === null ? null : (
            <span className={styles.scheduleCaption}>{`선점 ${row.ownerName}`}</span>
          )}
        </span>
      ),
    },
    {
      key: "requestedGpuLimit",
      header: "희망 한도",
      width: "120px",
      align: "right",
      sortable: "both",
      render: (row) => (
        <Text
          variant={hatcheryTextModes["body-normal"]}
          className={`${styles.numeric} ${styles.numericActive}`}
        >
          {row.requestedGpuLimit}
        </Text>
      ),
    },
    {
      // 증액 기간. 승인된 요청은 증액이 끝나는 시각을 함께 적는다. 그 시각이 지나면 한도가
      // 원래대로 돌아간다. 기간 없이 종결된 옛 요청은 증액이 없으므로 비운다.
      key: "requestedDays",
      header: "기간",
      // 승인된 행은 종료 시각(`2026-10-06 14:00 까지`)을 한 줄로 적는다. 그 줄이 잘리지 않는 폭이다.
      width: "160px",
      sortable: "both",
      render: (row) =>
        row.requestedDays === null ? (
          <span className={styles.numericIdle}>—</span>
        ) : (
          <span className={styles.resolution}>
            <Text variant={hatcheryTextModes["body-normal"]}>{`${row.requestedDays}일`}</Text>
            {row.grantEndsAtMs === null ? null : (
              <span className={styles.resolutionNote}>
                {`${formatDateTime(row.grantEndsAtMs)} 까지`}
              </span>
            )}
          </span>
        ),
    },
    {
      key: "reason",
      header: "사유",
      width: "minmax(200px, 1.4fr)",
      render: (row) => (
        <Text variant={hatcheryTextModes["body-normal"]} className={styles.reason}>
          {row.reason}
        </Text>
      ),
    },
    {
      key: "status",
      header: "상태",
      width: "120px",
      sortable: "both",
      render: (row) => <GpuQuotaRequestStatusChip status={row.status} />,
    },
    {
      // 요청자 이름과 접수 시각을 함께 적는다. 정렬은 접수 시각 기준이다. 대기열을 오래된 순으로
      // 보려는 정렬이기 때문이다.
      key: "createdAt",
      header: "요청",
      width: "minmax(130px, 170px)",
      sortable: "both",
      render: (row) => (
        <span className={styles.resolution}>
          <Text variant={hatcheryTextModes["body-normal"]}>{row.requestedBy}</Text>
          <span className={styles.resolutionNote}>{formatDateTime(parseUtcMs(row.createdAt))}</span>
        </span>
      ),
    },
    {
      key: "resolvedBy",
      header: "종결",
      width: "minmax(140px, 200px)",
      render: (row) => renderResolution(row),
    },
    {
      // 액션 컬럼. key 는 행 프로퍼티로 제한된다. 그래서 다른 컬럼이 쓰지 않는 requestUuid 를 쓴다.
      key: "requestUuid",
      header: "",
      width: "minmax(150px, 190px)",
      align: "right",
      render: (row) => renderActions(row, onCancel, onResolve),
    },
  ];
}

// 종결자와 메모. 거절 사유는 요청자가 다음 행동을 정하는 근거이므로 표에서 바로 표시한다.
function renderResolution(row: GpuQuotaRequestTableRow): ReactNode {
  if (row.resolvedBy === null) {
    return <span className={styles.numericIdle}>—</span>;
  }
  return (
    <span className={styles.resolution}>
      <Text variant={hatcheryTextModes["body-normal"]}>{row.resolvedBy}</Text>
      {row.resolutionNote === null ? null : (
        <span className={styles.resolutionNote}>{row.resolutionNote}</span>
      )}
    </span>
  );
}

// 가능한 동작은 서버가 준 canCancel/canResolve 만 본다. FE 는 등급과 소유를 다시 판정하지 않는다.
function renderActions(
  row: GpuQuotaRequestTableRow,
  onCancel: (row: GpuQuotaRequestTableRow) => void,
  onResolve: (row: GpuQuotaRequestTableRow, status: "applied" | "rejected") => void,
): ReactNode {
  if (!row.canCancel && !row.canResolve) {
    return null;
  }
  return (
    <span className={styles.rowActions}>
      {row.canResolve ? (
        <>
          <IconButton
            text="승인"
            size="sm"
            variant="solid"
            onClick={() => onResolve(row, "applied")}
          />
          <IconButton
            text="거절"
            size="sm"
            variant="surface"
            onClick={() => onResolve(row, "rejected")}
          />
        </>
      ) : null}
      {row.canCancel ? (
        <IconButton text="철회" size="sm" variant="surface" onClick={() => onCancel(row)} />
      ) : null}
    </span>
  );
}
